(() => {
  'use strict';

  const KEY = 'casa.recent-products.v1';
  const MAX_AGE = 30 * 24 * 60 * 60 * 1000;

  function cleanHistory(value, now = Date.now()) {
    if (!Array.isArray(value)) return [];

    const seen = new Set();

    return value.filter(item => {
      if (
        !item ||
        !/^\d+$/.test(String(item.id)) ||
        typeof item.handle !== 'string' ||
        !/^[a-z0-9-]+$/.test(item.handle) ||
        !Number.isFinite(item.time) ||
        item.time > now ||
        now - item.time > MAX_AGE ||
        seen.has(String(item.id))
      ) {
        return false;
      }

      seen.add(String(item.id));
      return true;
    }).slice(0, 20);
  }

  function candidates(history, cart) {
    const excluded = new Set(
      cart.items.map(item => String(item.product_id))
    );

    return cleanHistory(history)
      .filter(item => !excluded.has(String(item.id)))
      .slice(0, 6);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { cleanHistory, candidates };
    return;
  }

  function history() {
    try {
      return cleanHistory(
        JSON.parse(localStorage.getItem(KEY) || '[]')
      );
    } catch (_) {
      return [];
    }
  }

  try {
    const data = document.querySelector(
      '[data-casa-current-product]'
    );

    if (data) {
      const product = JSON.parse(data.textContent);

      const recent = history().filter(
        item => String(item.id) !== String(product.id)
      );

      recent.unshift({
        id: product.id,
        handle: product.handle,
        time: Date.now()
      });

      localStorage.setItem(
        KEY,
        JSON.stringify(cleanHistory(recent))
      );
    }
  } catch (_) {
    // Keep fallback recommendations if storage is unavailable.
  }

  if (customElements.get('casa-cart-recommendations')) return;

  class CasaCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.fallback = this.querySelector('[data-casa-fallback]');
      this.recent = this.querySelector('[data-casa-recent]');

      this.setNavigation();
      this.abort = new AbortController();

      this.addEventListener('click', event => {
        const button = event.target.closest(
          '[data-casa-direction]'
        );

        if (!button || !this.contains(button)) return;

        const list = this.recent.hidden
          ? this.fallback
          : this.recent;

        const step = (
          list.firstElementChild?.getBoundingClientRect().width ||
          list.clientWidth
        ) + 12;

        const direction = getComputedStyle(list).direction === 'rtl'
          ? -1
          : 1;

        list.scrollBy({
          left: direction *
            Number(button.dataset.casaDirection) *
            step,

          behavior: matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches ? 'auto' : 'smooth'
        });
      }, {
        signal: this.abort.signal
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.observer.disconnect();
            this.load();
          }
        });

        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abort?.abort();
    }

    setNavigation() {
      const list = this.recent.hidden
        ? this.fallback
        : this.recent;

      this.querySelectorAll(
        '[data-casa-direction]'
      ).forEach(button => {
        button.disabled = list.children.length < 2;
      });
    }

    async load() {
      const signal = this.abort.signal;
      const root = this.dataset.root || '/';

      try {
        const response = await fetch(root + 'cart.js', {
          signal,
          cache: 'no-store'
        });

        if (!response.ok) throw new Error('Cart unavailable');

        const cart = await response.json();

        const excluded = new Set(
          cart.items.map(item => String(item.product_id))
        );

        [...this.fallback.children].forEach(card => {
          if (excluded.has(card.dataset.productId)) {
            card.remove();
          }
        });

        const viewed = candidates(history(), cart);

        const results = await Promise.allSettled(
          viewed.map(async item => {
            const url = new URL(
              root + 'products/' + encodeURIComponent(item.handle),
              location.origin
            );

            url.searchParams.set(
              'section_id',
              'casa-recent-product'
            );

            const result = await fetch(url, { signal });

            if (!result.ok) return null;

            const doc = new DOMParser().parseFromString(
              await result.text(),
              'text/html'
            );

            const card = doc.querySelector(
              '[data-casa-recent-card]'
            );

            if (
              !card ||
              excluded.has(card.dataset.productId)
            ) {
              return null;
            }

            return card;
          })
        );

        if (!this.isConnected || signal.aborted) return;

        const cards = results
          .filter(result =>
            result.status === 'fulfilled' && result.value
          )
          .map(result => result.value);

        if (cards.length) {
          this.recent.replaceChildren(...cards);
          this.fallback.hidden = true;
          this.recent.hidden = false;

          this.querySelector(
            '[data-casa-rec-label]'
          ).textContent = 'RECENTLY VIEWED';
        }

        this.hidden = (
          this.recent.children.length +
          this.fallback.children.length
        ) === 0;

        this.setNavigation();
      } catch (_) {
        if (this.isConnected) {
          this.hidden = this.fallback.children.length === 0;
          this.setNavigation();
        }
      }
    }
  }

  customElements.define(
    'casa-cart-recommendations',
    CasaCartRecommendations
  );
})();